import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { trainingService, GpuType, PricingRow } from '../services/trainingApi';
import { formatGpuName } from '../lib/format';
import { Page, PageHeader, ConsoleTabs, Chip, Tag, Notice, TableCard, formatINR } from '../components/console/ui';
import { SearchIc } from '../components/console/icons';

// Prefill payload handed to the Instances page when a user clicks Launch.
export interface LaunchPrefill {
  gpu_type: string;
  region?: string;
  tier?: string;
}

const Gpus = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [gpuFilter, setGpuFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  const typesQ = useQuery({
    queryKey: ['gpu-types'],
    queryFn: () => trainingService.getGpuTypes().then((r) => r.data.data ?? []),
  });
  const pricingQ = useQuery({
    queryKey: ['pricing'],
    queryFn: () => trainingService.getPricing().then((r) => r.data.data ?? []),
  });

  const types: GpuType[] = useMemo(() => typesQ.data ?? [], [typesQ.data]);
  const pricing: PricingRow[] = useMemo(() => pricingQ.data ?? [], [pricingQ.data]);

  const vramByType = new Map<string, GpuType>(types.map((t) => [t.gpu_type, t]));

  const regions = useMemo(
    () => Array.from(new Set(pricing.map((p) => p.region))).sort(),
    [pricing]
  );
  const tiers = useMemo(() => Array.from(new Set(pricing.map((p) => p.tier))).sort(), [pricing]);
  const gpuTypeNames = useMemo(
    () => Array.from(new Set(pricing.map((p) => p.gpu_type))).sort(),
    [pricing]
  );

  const rows = pricing.filter((p) => {
    if (gpuFilter && p.gpu_type !== gpuFilter) return false;
    if (regionFilter && p.region !== regionFilter) return false;
    if (tierFilter && p.tier !== tierFilter) return false;
    if (search && !p.gpu_type.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const launch = (p: PricingRow) => {
    const prefill: LaunchPrefill = { gpu_type: p.gpu_type, region: p.region, tier: p.tier };
    navigate('/console/instances', { state: { prefill } });
  };

  const loading = typesQ.isLoading || pricingQ.isLoading;
  const vram = (gt?: GpuType) => (gt?.vram_gb ? `${gt.vram_gb} GB` : '-');
  const availability = (p: PricingRow) =>
    p.available ? (
      <Tag tone={p.instant_boot ? 'ok' : 'info'}>{p.instant_boot ? 'Instant' : 'Available'}</Tag>
    ) : (
      <Tag tone="off">Unavailable</Tag>
    );

  return (
    <Page>
      <PageHeader
        title="GPUs"
        subtitle="GPUs available for training. Prices are all-inclusive, in INR per hour."
      />
      <ConsoleTabs />

      {/* GPU type chips */}
      {gpuTypeNames.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="GPU type">
          <Chip on={!gpuFilter} onClick={() => setGpuFilter('')}>
            All GPUs
          </Chip>
          {gpuTypeNames.map((g) => (
            <Chip key={g} on={gpuFilter === g} onClick={() => setGpuFilter(g)}>
              {formatGpuName(g)}
            </Chip>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2.5">
        <label className="flex min-h-[40px] flex-[1_1_220px] items-center gap-2 rounded-md border border-ps-strong bg-ps-chrome px-3 text-ps-subtle focus-within:border-ps-accent sm:max-w-[280px]">
          <SearchIc />
          <span className="sr-only">Search GPUs</span>
          <input
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-ps-text placeholder-ps-subtle focus:outline-none focus:ring-0"
            placeholder="Search GPUs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="sr-only" htmlFor="gpu-region">
          Region
        </label>
        <select
          id="gpu-region"
          className="ps-input flex-[0_1_200px] !w-auto max-[640px]:flex-[1_1_100%]"
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
        >
          <option value="">All regions</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="gpu-tier">
          Tier
        </label>
        <select
          id="gpu-tier"
          className="ps-input flex-[0_1_160px] !w-auto max-[640px]:flex-[1_1_100%]"
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
        >
          <option value="">All tiers</option>
          {tiers.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <Notice>Loading GPU catalog…</Notice>
      ) : typesQ.isError || pricingQ.isError ? (
        <Notice tone="error">Could not load the GPU catalog.</Notice>
      ) : (
        <>
          {/* Desktop / tablet: full table. */}
          <TableCard
            className="hidden md:block"
            footer={`${rows.length} ${rows.length === 1 ? 'offer' : 'offers'} shown`}
          >
            <thead>
              <tr>
                <th>GPU</th>
                <th>VRAM</th>
                <th>Region</th>
                <th>Tier</th>
                <th>Availability</th>
                <th className="!text-right">Price / hr</th>
                <th>
                  <span className="sr-only">Launch</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const gt = vramByType.get(p.gpu_type);
                return (
                  <tr key={`${p.gpu_type}:${p.region}:${p.tier}:${i}`}>
                    <td className="font-medium text-ps-text">
                      {formatGpuName(p.gpu_type)}
                      {gt?.architecture && <span className="ml-2 text-xs font-normal text-ps-subtle">{gt.architecture}</span>}
                    </td>
                    <td className="text-ps-muted">{vram(gt)}</td>
                    <td className="text-ps-muted">{p.region}</td>
                    <td className="text-ps-muted">{p.tier}</td>
                    <td>{availability(p)}</td>
                    <td className="text-right font-semibold">{formatINR(p.price_per_hour)}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="ps-btn ps-btn-sm"
                        disabled={!p.available}
                        onClick={() => launch(p)}
                      >
                        Launch
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="!py-14 text-center text-ps-subtle">
                    No GPUs match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </TableCard>

          {/* Mobile: one card per GPU so every field is visible without scroll. */}
          <div className="ps-card divide-y divide-ps-raised md:hidden">
            {rows.map((p, i) => {
              const gt = vramByType.get(p.gpu_type);
              return (
                <div key={`${p.gpu_type}:${p.region}:${p.tier}:${i}`} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-medium text-ps-text">
                      {formatGpuName(p.gpu_type)}
                      {gt?.architecture && <span className="ml-2 text-xs font-normal text-ps-subtle">{gt.architecture}</span>}
                    </div>
                    <div className="whitespace-nowrap text-sm font-semibold text-ps-text">
                      {formatINR(p.price_per_hour)}
                      <span className="font-normal text-ps-subtle">/hr</span>
                    </div>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ps-muted">
                    <div>
                      <dt className="inline text-ps-subtle">VRAM: </dt>
                      {vram(gt)}
                    </div>
                    <div>
                      <dt className="inline text-ps-subtle">Region: </dt>
                      {p.region}
                    </div>
                    <div>
                      <dt className="inline text-ps-subtle">Tier: </dt>
                      {p.tier}
                    </div>
                    <div>{availability(p)}</div>
                  </dl>
                  <button
                    type="button"
                    className="ps-btn ps-btn-sm mt-3 w-full !min-h-[44px]"
                    disabled={!p.available}
                    onClick={() => launch(p)}
                  >
                    Launch
                  </button>
                </div>
              );
            })}
            {rows.length === 0 && (
              <div className="p-14 text-center text-[13px] text-ps-subtle">No GPUs match the current filters.</div>
            )}
          </div>
        </>
      )}
    </Page>
  );
};

export default Gpus;
